import React from "react";
import {
  Plane,
  Phone,
  Mail,
  School as SchoolIcon,
  User,
  FileText,
} from "lucide-react";
import Button from "@/components/Button";
import { StatusPill, MetaPill } from "./Pills";

export default function SchoolCard({ school, tab, onApprove, onReject, onReset, onEdit, onDelete }) {
  return (
    <div
      className="bg-white p-4 sm:p-5 rounded-2xl border border-sprouts-border shadow-soft mb-4"
      data-testid={`school-card-${school.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-extrabold text-sprouts-text">{school.school_name}</h3>
          <p className="text-xs text-sprouts-muted mt-0.5">
            {school.city}
            {school.state ? `, ${school.state}` : ""}
          </p>
        </div>
        <StatusPill status={school.status} />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-1.5">
        <MetaPill Icon={User} text={school.coordinator_name} />
        <MetaPill Icon={Mail} text={school.coordinator_email} />
      </div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        <MetaPill Icon={Phone} text={school.coordinator_mobile} />
        <MetaPill Icon={SchoolIcon} text={`Grades: ${school.grades_participating || "—"}`} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        <MetaPill Icon={FileText} text={`Expected: ${school.expected_entries ?? 0}`} />
      </div>

      <div className="mt-3 bg-sprouts-lavender rounded-xl p-3">
        <div className="flex items-center gap-1.5">
          <Plane size={14} className="text-sprouts-primary" />
          <span className="text-xs font-bold text-sprouts-text">
            Travel History:{" "}
            <span className={school.has_past_trips ? "text-sprouts-success" : "text-sprouts-muted"}>
              {school.has_past_trips
                ? `${(school.past_trips || []).length} past trip(s)`
                : "New to school travel"}
            </span>
          </span>
        </div>
        {school.has_past_trips && (school.past_trips || []).length > 0 && (
          <div className="mt-2 space-y-1.5">
            {school.past_trips.map((t, i) => (
              <div
                key={i}
                className="bg-white rounded-md px-2.5 py-1.5 border border-blue-100 text-xs text-sprouts-text"
              >
                <span className="font-black">{t.year}</span> · {t.destination} ·{" "}
                <span
                  className={`font-extrabold ${
                    t.trip_type === "International" ? "text-sprouts-secondary" : "text-sprouts-primary"
                  }`}
                >
                  {t.trip_type}
                </span>{" "}
                · {t.students_count} students
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {tab === "pending" && (
          <>
            <Button onClick={onApprove} data-testid={`approve-${school.id}`}>Approve</Button>
            <Button variant="secondary" onClick={onReject} data-testid={`reject-${school.id}`}>Reject</Button>
            <Button variant="outline" onClick={onEdit} data-testid={`edit-${school.id}`}>Edit</Button>
            <Button variant="danger" onClick={onDelete} data-testid={`delete-${school.id}`}>Delete</Button>
          </>
        )}
        {tab === "approved" && (
          <>
            <Button variant="secondary" onClick={onReject} data-testid={`reject-${school.id}`}>Reject</Button>
            <Button variant="outline" onClick={onEdit} data-testid={`edit-${school.id}`}>Edit</Button>
            <Button variant="danger" onClick={onDelete} data-testid={`delete-${school.id}`}>Delete</Button>
          </>
        )}
        {tab === "rejected" && (
          <>
            <Button onClick={onApprove} data-testid={`approve-${school.id}`}>Approve</Button>
            <Button variant="outline" onClick={onReset} data-testid={`reset-${school.id}`}>Move to Pending</Button>
            <Button variant="danger" onClick={onDelete} data-testid={`delete-${school.id}`}>Delete</Button>
          </>
        )}
      </div>
    </div>
  );
}
